import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Autocomplete, SxProps, TextFieldProps } from '@mui/material';

import { TPAutoCompleteArrowIconComponent, TPTextField } from '.';
import {
  TGetUserListByNameAsAdmin,
  getUserListForDropDownAsAdmin,
  getUsersState,
  resetUserListDataForAdmin,
} from '../../store/UserSlice';
import { useAppSelector } from '../../hooks';

export type TSelectOption = {
  label: string;
  value: string;
};

export interface UserListDropdownProps {
  disabled?: boolean;
  selectedItem: TSelectOption | null;
  textFieldProps?: TextFieldProps;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  componentProps?: any;
  handleChange: (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => void;
}

const defaultTextFieldProps: SxProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
};

const defaultFilterLimits = {
  limit: 30,
  offset: 0,
};

const UserListDropdown: React.FC<UserListDropdownProps> = ({
  selectedItem,
  componentProps = {},
  textFieldProps = {},
  handleChange,
  disabled = false,
}) => {
  const dispatch = useDispatch();

  // Store
  const userState = useAppSelector(getUsersState);

  // State
  const [options, setOptions] = useState<Array<TSelectOption>>([]);

  const { userListDataForAdminForDropDown } = userState;

  useEffect(() => {
    if (!disabled) {
      dispatch(
        getUserListForDropDownAsAdmin({
          ...defaultFilterLimits,
        })
      );
    }

    return () => {
      dispatch(resetUserListDataForAdmin());
    };
  }, []);

  useEffect(() => {
    if (
      userListDataForAdminForDropDown &&
      userListDataForAdminForDropDown.list &&
      userListDataForAdminForDropDown.list.length
    ) {
      const newOptions: TSelectOption[] = [];

      userListDataForAdminForDropDown.list.forEach((item) => {
        let _name = item.firstname;

        if (item.lastname) {
          _name += ` ${item.lastname}`;
        }

        if (item.role && item.role.name) {
          _name += ` (${item.role.name})`;
        }

        newOptions.push({
          label: _name,
          value: item._id,
        });
      });

      setOptions(newOptions);
    }
  }, [userListDataForAdminForDropDown]);

  const handleInputChange = (
    event: React.SyntheticEvent<Element, Event>,
    newInputValue: string
  ) => {
    const apiData: TGetUserListByNameAsAdmin = {
      ...defaultFilterLimits,
    };

    if (newInputValue.trim().length) {
      apiData.name = newInputValue.trim();
    }

    dispatch(getUserListForDropDownAsAdmin(apiData));
  };

  return (
    <Autocomplete
      disabled={disabled}
      value={selectedItem}
      options={options}
      onChange={handleChange}
      onInputChange={handleInputChange}
      popupIcon={TPAutoCompleteArrowIconComponent}
      renderInput={(params) => (
        <TPTextField
          {...params}
          size="small"
          placeholder="Select a User"
          {...defaultTextFieldProps}
          {...textFieldProps}
        />
      )}
      {...componentProps}
    />
  );
};

export default UserListDropdown;
